import type { Theme } from './types';
import { cssFontFamily } from './fonts';

const cssUrl = (value: string): string => value ? `url("${value.replace(/["\\]/g, '')}")` : 'none';
const whiteBacked = (value: string): string => `linear-gradient(${value}, ${value}), #FFFFFF`;

const fontLoaderFamilies = new Set<string>();
let fontLoaderAttempts = 0;

const loadFonts = (families: string[]): void => {
	families.filter(Boolean).forEach((family) => fontLoaderFamilies.add(family));
	if (window.SocialPointFontLoader) {
		window.SocialPointFontLoader.load(Array.from(fontLoaderFamilies));
		fontLoaderFamilies.clear();
		return;
	}

	const scriptId = 'new-trivia-font-loader';
	if (document.getElementById(scriptId)) return;
	const script = document.createElement('script');
	script.id = scriptId;
	script.src = '/apps/assets/font-loader.js?q=20260817170000';
	script.async = true;
	script.addEventListener('load', () => {
		fontLoaderAttempts = 0;
		window.SocialPointFontLoader?.load(Array.from(fontLoaderFamilies));
		fontLoaderFamilies.clear();
	}, { once: true });
	script.addEventListener('error', () => {
		script.remove();
		fontLoaderAttempts++;
		if (fontLoaderAttempts < 3) {
			window.setTimeout(() => loadFonts([]), fontLoaderAttempts * 750);
			return;
		}
		fontLoaderAttempts = 0;
		window.dispatchEvent(new CustomEvent('socialpoint:font-load-error', { detail: { stage: 'script' } }));
	}, { once: true });
	document.head.appendChild(script);
};

export const applyTheme = (theme: Theme): void => {
	document.getElementById('new-trivia-runtime-theme')?.remove();
	loadFonts([
		theme.fontFamily,
		theme.secondaryFontFamily,
		theme.buttonFontFamily,
		theme.leaderboardFontFamily,
	]);
	const primaryFont = cssFontFamily(theme.fontFamily);
	const secondaryFont = cssFontFamily(theme.secondaryFontFamily);
	const buttonFont = cssFontFamily(theme.buttonFontFamily);
	const leaderboardFont = cssFontFamily(theme.leaderboardFontFamily);
	const pageBackground = theme.appBackground;
	const panelBackground = theme.surfaceBackground;
	const contentCardBackground = theme.secondarySurfaceBackground;
	const categoryCardBackground = theme.allowCardGradients
		? `linear-gradient(145deg, ${contentCardBackground}, ${theme.cardGradientEnd})`
		: contentCardBackground;
	const heroShadow = `0 ${Math.round(16 * theme.shadowDepthMultiplier)}px ${Math.round(38 * theme.shadowBlurMultiplier)}px ${theme.shadowColor}`;
	const primaryButtonShadow = `0 ${Math.round(12 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.primaryButtonActiveShadow}`;
	const style = document.createElement('style');
	style.id = 'new-trivia-runtime-theme';
	style.textContent = `
		html {
			background-color: ${pageBackground};
		}
		body {
			background-color: ${pageBackground};
			background-image: ${cssUrl(theme.backgroundImageUrl)};
		}
		#new-trivia-app-root {
			background: ${panelBackground};
			border-color: ${theme.borderColor};
			box-shadow: 0 ${Math.round(18 * theme.shadowDepthMultiplier)}px ${Math.round(46 * theme.shadowBlurMultiplier)}px ${theme.shadowColor};
			color: ${theme.textColor};
			font-family: ${primaryFont};
		}
		#new-trivia-app-root .app-main { background: transparent; }
		#new-trivia-app-root .app-header { background: ${theme.headerBackground}; border-color: ${theme.borderColor}; }
		#new-trivia-app-root h1, #new-trivia-app-root h2 { font-family: ${secondaryFont}; }
		#new-trivia-app-root .player-card {
			background: ${contentCardBackground};
			border-color: ${theme.borderColor};
			box-shadow: 0 ${Math.round(12 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.shadowColor};
		}
		#new-trivia-app-root .reward-popup-card {
			background: ${theme.secondarySurfaceBackground};
			color: ${theme.primaryText};
		}
		#new-trivia-app-root .reward-popup-message,
		#new-trivia-app-root .reward-popup-prize span {
			color: ${theme.mutedTextColor};
		}
		#new-trivia-app-root .reward-popup-prize strong {
			color: ${theme.primaryText};
		}
		#new-trivia-app-root .category-selector-card { background: ${categoryCardBackground}; }
		#new-trivia-app-root .game-hero {
			background: linear-gradient(${theme.heroScrimColor}, ${theme.heroScrimColor}), linear-gradient(${theme.heroGradientAngle}, ${theme.primaryColor}, ${theme.heroGradientEnd});
			border-radius: ${theme.heroRadius}px;
			box-shadow: ${heroShadow};
			color: ${theme.heroSecondaryTextColor};
		}
		#new-trivia-app-root .game-hero h1 { color: ${theme.heroTitleColor}; }
		#new-trivia-app-root .game-hero p { color: ${theme.heroSecondaryTextColor}; }
		#new-trivia-app-root .player-card,
		#new-trivia-app-root .mode-stat,
		#new-trivia-app-root .clash-score,
		#new-trivia-app-root .bingo-tile,
		#new-trivia-app-root .summary-item { border-radius: ${theme.cardRadius}px; }
		#new-trivia-app-root .leaderboard-table tbody td:first-child { border-radius: ${theme.cardRadius}px 0 0 ${theme.cardRadius}px; }
		#new-trivia-app-root .leaderboard-table tbody td:last-child { border-radius: 0 ${theme.cardRadius}px ${theme.cardRadius}px 0; }
		#new-trivia-app-root .leaderboard-table tbody td:only-child { border-radius: ${theme.cardRadius}px; }
		#new-trivia-app-root .game-hero-logo,
		#new-trivia-app-root .reward-popup-image { border-radius: ${theme.heroRadius}px; }
		#new-trivia-app-root .quiz-prompt-panel { border-radius: ${theme.cardRadius}px ${theme.cardRadius}px 0 0; }
		#new-trivia-app-root .quiz-answer-panel { border-radius: 0 0 ${theme.cardRadius}px ${theme.cardRadius}px; }
		@media (orientation: landscape) and (min-width: 900px) {
			#new-trivia-app-root .quiz-prompt-panel,
			#new-trivia-app-root .quiz-answer-panel { border-radius: ${theme.cardRadius}px; }
		}
		#new-trivia-app-root .login-card { background: ${theme.loginCardBackground}; color: ${theme.loginTextColor}; }
		#new-trivia-app-root .agreement-modal-content { background: ${contentCardBackground}; border-color: ${theme.borderColor}; color: ${theme.textColor}; }
		#new-trivia-app-root .agreement-modal-header, #new-trivia-app-root .agreement-modal-footer { border-color: ${theme.borderColor}; }
		#new-trivia-app-root .quiz-prompt-panel,
		#new-trivia-app-root .quiz-answer-panel { background: ${theme.questionPanelBackground}; border-color: ${theme.borderColor}; }
		#new-trivia-app-root .question-image { background: ${theme.questionImageBackground}; }
		#new-trivia-app-root .final-card { background: ${theme.finalCardBackground}; color: ${theme.primaryText}; }
		#new-trivia-app-root .leaderboard-card,
		#new-trivia-app-root .leaderboard-table {
			background: ${theme.leaderboardBackground};
			color: ${theme.leaderboardTextColor};
			font-family: ${leaderboardFont};
		}
		#new-trivia-app-root .leaderboard-card h1,
		#new-trivia-app-root .final-full-leaderboard { font-family: ${leaderboardFont}; }
		#new-trivia-app-root .muted, #new-trivia-app-root .question-count,
		#new-trivia-app-root .template-note,
		#new-trivia-app-root .mode-stat span { color: ${theme.mutedTextColor}; }
		#new-trivia-app-root .primary-action, #new-trivia-app-root .secondary-action {
			border-radius: ${theme.buttonRadius}px;
			border-style: solid;
			border-width: ${theme.buttonBorderWidth}px;
			font-family: ${buttonFont};
		}
		#new-trivia-app-root .primary-action {
			background: ${theme.primaryButtonGradient === 'none' ? theme.primaryButtonBackground : theme.primaryButtonGradient};
			border-color: ${theme.primaryButtonBorder};
			color: ${theme.primaryButtonText};
			box-shadow: ${primaryButtonShadow};
		}
		#new-trivia-app-root .primary-action:not(:disabled):hover,
		#new-trivia-app-root .primary-action:not(:disabled):focus,
		#new-trivia-app-root .primary-action:not(:disabled):active { background: ${theme.primaryButtonHover}; box-shadow: ${primaryButtonShadow}; }
		#new-trivia-app-root .primary-action:disabled { box-shadow: ${primaryButtonShadow}; }
		#new-trivia-app-root .secondary-action {
			background: ${theme.secondaryButtonBackground};
			border-color: ${theme.secondaryButtonBorder};
			color: ${theme.secondaryButtonText};
		}
		#new-trivia-app-root .secondary-action:not(:disabled):hover,
		#new-trivia-app-root .secondary-action:not(:disabled):focus { background: ${theme.secondaryButtonHover}; }
		#new-trivia-app-root button:disabled,
		#new-trivia-app-root input:disabled:not(.guess-input),
		#new-trivia-app-root select:disabled,
		#new-trivia-app-root textarea:disabled { opacity: ${theme.disabledOpacity}; }
		#new-trivia-app-root .answer-option:disabled,
		#new-trivia-app-root .clash-tile:disabled,
		#new-trivia-app-root .bingo-tile:disabled { opacity: 1; }
		#new-trivia-app-root .home-action {
			border-color: ${theme.borderColor};
		}
		#new-trivia-app-root input, #new-trivia-app-root select, #new-trivia-app-root textarea,
		#new-trivia-app-root .confirmation-list, #new-trivia-app-root .ranking-option,
		#new-trivia-app-root .summary-item {
			background: ${theme.summaryItemBackground};
			border-color: ${theme.summaryItemBorder};
			color: ${theme.primaryText};
		}
		#new-trivia-app-root input, #new-trivia-app-root select, #new-trivia-app-root textarea {
			background: ${theme.inputBackground};
			border-color: ${theme.inputBorder};
			border-radius: ${theme.inputRadius}px;
			box-shadow: 0 0 4px ${theme.inputShadow};
			color: ${theme.inputText};
		}
		#new-trivia-app-root input::placeholder, #new-trivia-app-root textarea::placeholder { color: ${theme.placeholderText}; }
		#new-trivia-app-root .error-banner, #new-trivia-app-root .media-error {
			background: ${theme.incorrectBackground};
			border-color: ${theme.incorrectBorder};
			color: ${theme.incorrectText};
		}
		#new-trivia-app-root .field-error, #new-trivia-app-root .validation-list { color: ${theme.errorColor}; }
		#new-trivia-app-root .confirmation-list dt { color: ${theme.mutedTextColor}; }
		#new-trivia-app-root .embedded-survey,
		#new-trivia-app-root .leaderboard-table th, #new-trivia-app-root .leaderboard-table td {
			border-color: ${theme.borderColor};
		}
		#new-trivia-app-root .answer-option {
			background: ${whiteBacked(theme.unselectedBackground)};
			border-color: ${theme.unselectedBorder};
			border-radius: ${theme.inputRadius}px;
			box-shadow: 0 0 4px ${theme.unselectedGlow};
			color: ${theme.unselectedText};
		}
		#new-trivia-app-root .answer-option.selected {
			background: ${whiteBacked(theme.selectedBackground)};
			border-color: ${theme.selectedBorder};
			box-shadow: 0 0 0 4px ${theme.selectedGlow};
			color: ${theme.selectedText};
		}
		#new-trivia-app-root .answer-option.correct {
			background: ${whiteBacked(theme.correctBackground)};
			border-color: ${theme.correctBorder};
			box-shadow: 0 0 0 4px ${theme.correctGlow};
			color: ${theme.correctText};
		}
		#new-trivia-app-root .answer-option.incorrect {
			background: ${whiteBacked(theme.incorrectBackground)};
			border-color: ${theme.incorrectBorder};
			box-shadow: 0 0 0 4px ${theme.incorrectGlow};
			color: ${theme.incorrectText};
		}
		#new-trivia-app-root .answer-letter { background: ${theme.answerLetterUnselectedBackground}; border-color: ${theme.answerLetterUnselectedBorder}; color: ${theme.answerLetterUnselectedText}; }
		#new-trivia-app-root .answer-option.selected .answer-letter { background: ${theme.answerLetterSelectedBackground}; color: ${theme.answerLetterSelectedText}; }
		#new-trivia-app-root .answer-option.correct .answer-letter { background: ${theme.answerLetterCorrectBackground}; color: ${theme.answerLetterCorrectText}; }
		#new-trivia-app-root .answer-option.incorrect .answer-letter { background: ${theme.answerLetterIncorrectBackground}; color: ${theme.answerLetterIncorrectText}; }
		#new-trivia-app-root .ranking-move-button {
			background: ${theme.secondaryButtonBackground};
			border-color: ${theme.secondaryButtonBorder};
			color: ${theme.secondaryButtonText};
		}
		#new-trivia-app-root .ranking-option.drag-target { border-color: ${theme.primaryColor}; }
		#new-trivia-app-root .answer-option.correct .ranking-move-button {
			background: ${theme.answerLetterCorrectBackground};
			border-color: ${theme.correctBorder};
			color: ${theme.answerLetterCorrectText};
		}
		#new-trivia-app-root .answer-option.incorrect .ranking-move-button {
			background: ${theme.answerLetterIncorrectBackground};
			border-color: ${theme.incorrectBorder};
			color: ${theme.answerLetterIncorrectText};
		}
		#new-trivia-app-root .quiz-prompt-panel,
		#new-trivia-app-root .quiz-answer-panel {
			background: ${theme.questionPanelBackground};
			border-color: ${theme.borderColor};
		}
		@media (max-width: 899px), (orientation: portrait) {
			#new-trivia-app-root .question-card {
				border-radius: ${theme.cardRadius}px;
				box-shadow: 0 ${Math.round(12 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.shadowColor};
			}
		}
		#new-trivia-app-root .letter-box, #new-trivia-app-root .timer-value,
		#new-trivia-app-root .video-waiting { background: ${theme.secondarySurfaceBackground}; border-color: ${theme.borderColor}; color: ${theme.textColor}; }
		#new-trivia-app-root .letter-box.is-filled { background: ${theme.selectedBackground}; border-color: ${theme.selectedBorder}; }
		#new-trivia-app-root .category-selector-helper,
		#new-trivia-app-root .category-spin-note,
		#new-trivia-app-root .category-reveal-label { color: ${theme.mutedTextColor}; }
		#new-trivia-app-root .category-wheel-pointer::before {
			background: linear-gradient(180deg, ${theme.elevatedSurface} 0%, ${theme.secondarySurfaceBackground} 35%, ${theme.primaryButtonBackground} 100%);
		}
		#new-trivia-app-root .mode-stat {
			background: ${contentCardBackground};
			border-color: ${theme.borderColor};
			box-shadow: 0 ${Math.round(4 * theme.shadowDepthMultiplier)}px ${Math.round(12 * theme.shadowBlurMultiplier)}px ${theme.softShadowColor};
		}
		#new-trivia-app-root .clash-board-heading .mode-board-header h1 { color: ${theme.categoryClashBoardTitleColor}; }
		#new-trivia-app-root .clash-board-heading .mode-board-header p { color: ${theme.categoryClashQuestionsCompletedLabelColor}; }
		#new-trivia-app-root .clash-score {
			background: ${theme.categoryClashHeaderStatsBackground};
			border-color: ${theme.categoryClashHeaderStatsBorder};
			color: ${theme.categoryClashHeaderStatsText};
		}
		#new-trivia-app-root .clash-score span,
		#new-trivia-app-root .clash-score strong { color: ${theme.categoryClashHeaderStatsText}; }
		#new-trivia-app-root .timer-fill { background: ${theme.successColor}; }
		#new-trivia-app-root .question-timer.is-warning .timer-fill { background: ${theme.errorColor}; }
		#new-trivia-app-root .question-timer.is-warning .timer-value { border-color: ${theme.errorColor}; color: ${theme.errorColor}; }
		#new-trivia-app-root .header-status,
		#new-trivia-app-root .score-pill {
			background: ${theme.pillBackground};
			border-color: ${theme.pillBorder};
			color: ${theme.pillText};
		}
		#new-trivia-app-root .category-pill { background: ${theme.categoryLabelBackground}; color: ${theme.categoryLabelText}; }
		#new-trivia-app-root .clash-board {
			background: ${theme.categoryClashBoardBackground};
			box-shadow: 0 ${Math.round(12 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.shadowColor};
		}
		#new-trivia-app-root .clash-category { background: ${theme.categoryClashCategoryBackground}; border: 1px solid ${theme.categoryClashCategoryBorder}; color: ${theme.categoryClashCategoryText}; }
		#new-trivia-app-root .clash-tile { background: ${whiteBacked(theme.categoryClashTileBackground)}; color: ${theme.categoryClashTileText}; }
		#new-trivia-app-root .clash-tile:not(:disabled):hover,
		#new-trivia-app-root .clash-tile:not(:disabled):focus-visible { box-shadow: 0 0 0 4px ${theme.focusGlow}; }
		#new-trivia-app-root .clash-tile.correct { background: ${whiteBacked(theme.correctBackground)}; border: 1px solid ${theme.correctBorder}; color: ${theme.correctText}; }
		#new-trivia-app-root .clash-tile.incorrect { background: ${whiteBacked(theme.incorrectBackground)}; border: 1px solid ${theme.incorrectBorder}; color: ${theme.incorrectText}; }
		#new-trivia-app-root .bingo-board {
			background: ${theme.bingoBoardBackground};
			border-color: ${theme.bingoBoardBorder};
			box-shadow: 0 ${Math.round(12 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.shadowColor};
		}
		#new-trivia-app-root .bingo-tile { background: ${whiteBacked(theme.unselectedBackground)}; border-color: ${theme.unselectedBorder}; box-shadow: 0 0 4px ${theme.unselectedGlow}; color: ${theme.unselectedText}; }
		#new-trivia-app-root .bingo-tile.marked { background: ${whiteBacked(theme.correctBackground)}; border-color: ${theme.correctBorder}; box-shadow: 0 0 0 4px ${theme.correctGlow}; color: ${theme.correctText}; }
		#new-trivia-app-root .bingo-tile.attempted:not(.marked) { background: ${whiteBacked(theme.incorrectBackground)}; border-color: ${theme.incorrectBorder}; box-shadow: 0 0 0 4px ${theme.incorrectGlow}; color: ${theme.incorrectText}; }
		#new-trivia-app-root .bingo-line { background: ${theme.bingoLineColor}; box-shadow: 0 0 12px ${theme.bingoLineColor}; }
		#new-trivia-app-root .bingo-line::before, #new-trivia-app-root .bingo-line::after { color: ${theme.categoryClashCategoryText}; }
		#new-trivia-app-root .bingo-bonus-points strong { color: ${theme.primaryColor}; }
		#new-trivia-app-root .feedback-correct { color: ${theme.successColor}; }
		#new-trivia-app-root .feedback-icon.feedback-correct { background: ${theme.answerLetterCorrectBackground}; color: ${theme.answerLetterCorrectText}; }
		#new-trivia-app-root .feedback-icon.feedback-incorrect { background: ${theme.answerLetterIncorrectBackground}; color: ${theme.answerLetterIncorrectText}; }
		#new-trivia-app-root .final-card .feedback-icon.feedback-correct { background: ${theme.summaryItemBackground}; border-color: ${theme.summaryItemBorder}; color: ${theme.finalScoreColor}; }
		#new-trivia-app-root .feedback-incorrect, #new-trivia-app-root .error-text { color: ${theme.errorColor}; }
		#new-trivia-app-root .question-feedback.correct { background: ${whiteBacked(theme.correctBackground)}; border-color: ${theme.correctBorder}; color: ${theme.correctText}; }
		#new-trivia-app-root .question-feedback.incorrect { background: ${whiteBacked(theme.incorrectBackground)}; border-color: ${theme.incorrectBorder}; color: ${theme.incorrectText}; }
		#new-trivia-app-root .question-feedback.partial { background: ${whiteBacked(theme.selectedBackground)}; border-color: ${theme.selectedBorder}; color: ${theme.selectedText}; }
		#new-trivia-app-root .question-feedback { border-radius: ${theme.inputRadius}px; }
		#new-trivia-app-root .summary-item strong { color: ${theme.finalScoreColor}; }
		#new-trivia-app-root .leaderboard-table th { background: transparent; color: ${theme.leaderboardTextColor}; }
		#new-trivia-app-root .leaderboard-table tbody td { background: ${theme.leaderboardRowBackground}; border-color: ${theme.leaderboardRowBorder}; color: ${theme.leaderboardRowText}; }
		#new-trivia-app-root .leaderboard-table tr.you td { background: ${theme.leaderboardYouBackground}; border-color: ${theme.leaderboardYouBorder}; color: ${theme.leaderboardYouText}; }
		#new-trivia-app-root .login-card a,
		#new-trivia-app-root .game-hero a { color: ${theme.loginLinkColor}; }
		#new-trivia-app-root .login-card a:hover,
		#new-trivia-app-root .game-hero a:hover { color: ${theme.linkHoverColor}; }
		#new-trivia-app-root .loading-spinner { border-color: ${theme.borderColor}; border-top-color: ${theme.primaryColor}; }
		#new-trivia-app-root .reward-popup-icon { background: ${theme.selectedBackground}; color: ${theme.primaryColor}; }
		#new-trivia-app-root input:focus, #new-trivia-app-root select:focus,
		#new-trivia-app-root textarea:focus {
			border-color: ${theme.primaryColor};
			box-shadow: 0 0 0 4px ${theme.focusGlow};
			outline: none;
		}
		#new-trivia-app-root .guess-region:focus-within .letter-box { box-shadow: 0 0 0 4px ${theme.focusGlow}; }
		#new-trivia-app-root .player-confirmation-overlay,
		#new-trivia-app-root .reward-popup-overlay,
		#new-trivia-app-root .category-reveal-overlay { background: ${theme.overlayColor}; }
		#new-trivia-app-root .confirmation-dialog { background: ${contentCardBackground}; border-color: ${theme.borderColor}; box-shadow: 0 ${Math.round(14 * theme.shadowDepthMultiplier)}px ${Math.round(34 * theme.shadowBlurMultiplier)}px ${theme.shadowColor}; }
		#new-trivia-app-root .summary-item span { color: ${theme.summaryItemLabelText}; }
	`;
	document.head.appendChild(style);
	document.getElementById('new-trivia-app-root')?.setAttribute('data-personality', theme.personality);
	document.getElementById('new-trivia-app-root')?.setAttribute('data-theme-engine', String(theme.engineVersion));
	document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.primaryColor);
};
